import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Grid, 
  CircularProgress,
  useTheme,
  Avatar,
  Chip
} from '@mui/material';
import { 
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  Lock as LockIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import { supabase } from '../../../lib/supabase';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title, 
  Tooltip, 
  Legend,
  Filler
} from 'chart.js';
import { Doughnut, Bar, Line } from 'react-chartjs-2';
import { format, subWeeks, startOfWeek, endOfWeek } from 'date-fns';

// Register Chart.js components
ChartJS.register(
  CategoryScale, 
  LinearScale, 
  BarElement, 
  ArcElement,
  PointElement,
  LineElement,
  Title, 
  Tooltip, 
  Legend,
  Filler
);

const ReputationChartsPrivate = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [stats, setStats] = useState({
    averageRating: 0,
    totalFeedback: 0,
    positivePercentage: 0,
    responseRate: 0
  });

  useEffect(() => {
    fetchPrivateAnalytics();
  }, []);

  const fetchPrivateAnalytics = async () => {
    try {
      setLoading(true);
      
      // Try RPC first (most efficient - single query with server-side aggregation)
      const { data: rpcData, error: rpcError } = await supabase.rpc('get_private_feedback_analytics');
      
      if (!rpcError && rpcData) {
        console.log('[ReputationChartsPrivate] Using RPC data:', rpcData);
        setAnalyticsData(rpcData);
        
        const { stats: rpcStats } = rpcData;
        const totalFeedback = rpcStats.totalFeedback || 0;
        setStats({
          averageRating: parseFloat(rpcStats.averageRating) || 0,
          totalFeedback,
          positivePercentage: totalFeedback > 0 ? ((rpcStats.positiveCount / totalFeedback) * 100).toFixed(0) : 0,
          responseRate: totalFeedback > 0 ? ((rpcStats.withComments / totalFeedback) * 100).toFixed(0) : 0
        });
        return;
      }
      
      // Fallback: fetch minimal data if RPC not available
      console.log('[ReputationChartsPrivate] RPC not available, using fallback');
      const { data, error } = await supabase
        .from('feedback')
        .select('rating, created_at, comment', { count: 'exact' })
        .eq('is_public', false)
        .order('created_at', { ascending: false })
        .limit(500);

      if (error) throw error;

      // Build analytics from limited data
      const fallbackAnalytics = buildFallbackAnalytics(data || []);
      setAnalyticsData(fallbackAnalytics);
      calculateStats(data || []);
    } catch (error) {
      console.error('Error fetching private feedback:', error);
    } finally {
      setLoading(false);
    }
  };

  const buildFallbackAnalytics = (data) => {
    const ratingDist = { '1': 0, '2': 0, '3': 0, '4': 0, '5': 0 };
    data.forEach(item => {
      if (item.rating >= 1 && item.rating <= 5) {
        ratingDist[item.rating.toString()]++;
      }
    });
    return { ratingDistribution: ratingDist, weeklyTrend: [] };
  };

  const calculateStats = (data) => {
    if (data.length === 0) {
      setStats({ averageRating: 0, totalFeedback: 0, positivePercentage: 0, responseRate: 0 });
      return;
    }
    const totalRating = data.reduce((sum, item) => sum + (item.rating || 0), 0);
    const averageRating = (totalRating / data.length).toFixed(1);
    const positiveCount = data.filter(item => item.rating >= 4).length;
    const feedbackWithComments = data.filter(item => item.comment && item.comment.trim() !== '').length;
    
    setStats({
      averageRating: parseFloat(averageRating),
      totalFeedback: data.length,
      positivePercentage: ((positiveCount / data.length) * 100).toFixed(0),
      responseRate: ((feedbackWithComments / data.length) * 100).toFixed(0)
    });
  };

  // Rating Distribution Chart Data (uses pre-aggregated data from RPC)
  const getRatingDistributionData = () => {
    const dist = analyticsData?.ratingDistribution || { '1': 0, '2': 0, '3': 0, '4': 0, '5': 0 };
    const ratingCounts = [
      parseInt(dist['1']) || 0,
      parseInt(dist['2']) || 0,
      parseInt(dist['3']) || 0,
      parseInt(dist['4']) || 0,
      parseInt(dist['5']) || 0
    ];

    return {
      labels: ['1★', '2★', '3★', '4★', '5★'],
      datasets: [{
        label: 'Feedback',
        data: ratingCounts,
        backgroundColor: [
          '#F44336',
          '#FF9800',
          '#FFC107',
          '#8BC34A',
          '#4CAF50',
        ],
        borderWidth: 0
      }]
    };
  };

  // Provider Performance Chart Data (uses pre-aggregated data from RPC)
  const getProviderPerformanceData = () => {
    const providerPerformance = analyticsData?.providerPerformance || [];
    
    if (providerPerformance.length > 0) {
      return {
        labels: providerPerformance.map(p => p.provider_name),
        datasets: [{
          label: 'Average Rating',
          data: providerPerformance.map(p => parseFloat(p.avg_rating) || 0),
          backgroundColor: providerPerformance.map(p => {
            const rating = parseFloat(p.avg_rating) || 0;
            if (rating >= 4.5) return '#4CAF50';
            if (rating >= 4.0) return '#8BC34A';
            if (rating >= 3.5) return '#FFC107';
            return '#FF9800';
          }),
          borderWidth: 1,
          borderColor: theme.palette.divider
        }]
      };
    }
    
    // Fallback: empty chart
    return {
      labels: ['Provider 1', 'Provider 2', 'Provider 3'],
      datasets: [{
        label: 'Average Rating',
        data: [0, 0, 0],
        backgroundColor: ['#4CAF50', '#8BC34A', '#FFC107'],
        borderWidth: 1,
        borderColor: theme.palette.divider
      }]
    };
  };

  // Feedback Volume Chart Data (Last 8 weeks - uses pre-aggregated RPC data)
  const getFeedbackVolumeData = () => {
    const weeklyTrend = analyticsData?.weeklyTrend || [];
    
    // Use RPC weekly data if available
    if (weeklyTrend.length > 0) {
      return {
        labels: weeklyTrend.map(w => w.week_name),
        datasets: [{
          label: 'Feedback Count',
          data: weeklyTrend.map(w => parseInt(w.count) || 0),
          borderColor: theme.palette.secondary.main,
          backgroundColor: `${theme.palette.secondary.main}40`,
          fill: true,
          tension: 0.4
        }]
      };
    }
    
    // Fallback: generate empty 8 week chart
    const weeksData = [];
    const today = new Date();
    for (let i = 7; i >= 0; i--) {
      const weekDate = subWeeks(today, i);
      weeksData.push({
        week: format(weekDate, 'MMM d'),
        count: 0
      });
    }

    return {
      labels: weeksData.map(w => w.week),
      datasets: [{
        label: 'Feedback Count',
        data: weeksData.map(w => w.count),
        borderColor: theme.palette.secondary.main,
        backgroundColor: `${theme.palette.secondary.main}40`,
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6
      }]
    };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Stats Cards Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Avatar sx={{ bgcolor: '#9C27B0', width: 40, height: 40, mx: 'auto', mb: 1 }}>
                <StarIcon fontSize="small" />
              </Avatar>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#9C27B0' }}>
                {stats.averageRating}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Avg Rating
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Avatar sx={{ bgcolor: theme.palette.secondary.main, width: 40, height: 40, mx: 'auto', mb: 1 }}>
                <LockIcon fontSize="small" />
              </Avatar>
              <Typography variant="h4" sx={{ fontWeight: 600, color: theme.palette.secondary.main }}>
                {stats.totalFeedback}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total Feedback
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Avatar sx={{ bgcolor: '#4CAF50', width: 40, height: 40, mx: 'auto', mb: 1 }}>
                <TrendingUpIcon fontSize="small" />
              </Avatar>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#4CAF50' }}>
                {stats.positivePercentage}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Positive (4-5★)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Avatar sx={{ bgcolor: '#2196F3', width: 40, height: 40, mx: 'auto', mb: 1 }}>
                <PeopleIcon fontSize="small" />
              </Avatar>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#2196F3' }}>
                {stats.responseRate}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                With Comments
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3}>
        {/* Rating Distribution */}
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: theme.palette.text.primary }}>
                Rating Distribution
              </Typography>
              <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {stats.totalFeedback > 0 ? (
                  <Doughnut 
                    data={getRatingDistributionData()}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'bottom',
                          labels: {
                            boxWidth: 12,
                            padding: 10,
                            font: { size: 10 }
                          }
                        }
                      }
                    }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">No data available</Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Provider Performance */}
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: theme.palette.text.primary }}>
                Provider Performance
              </Typography>
              <Box sx={{ height: 200 }}>
                {stats.totalFeedback > 0 ? (
                  <Bar 
                    data={getProviderPerformanceData()}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      indexAxis: 'y',
                      plugins: {
                        legend: { display: false }
                      },
                      scales: {
                        x: {
                          beginAtZero: true,
                          max: 5,
                          ticks: { stepSize: 1 }
                        }
                      }
                    }}
                  />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography variant="body2" color="text.secondary">No data available</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Feedback Volume Trend */}
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: theme.palette.text.primary }}>
                Feedback Volume (8 Weeks)
              </Typography>
              <Box sx={{ height: 200 }}>
                {stats.totalFeedback > 0 ? (
                  <Line 
                    data={getFeedbackVolumeData()}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: false }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: { precision: 0 }
                        }
                      }
                    }}
                  />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography variant="body2" color="text.secondary">No data available</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ReputationChartsPrivate;
